<template>
  <FormModal
    :visible="visible"
    :title="isEditing ? 'Editar procurador' : 'Adicionar procurador'"
    :icon="require('@/assets/addRepresentante.png')"
    :loading="loading"
    :confirm-label="isEditing ? 'Atualizar' : 'Adicionar'"
    @update:visible="handleClose"
    @confirm="handleSubmit"
    @cancel="handleClose"
  >
    <form @submit.prevent="handleSubmit" class="space-y-6">
      <!-- SUBSECTION: Dados Pessoais -->
      <div class="form-subsection">
        <h4 class="form-subsection-title">
          <i class="fa fa-user"></i>
          Dados Pessoais
        </h4>

        <div class="form-grid-1">
          <div class="form-field">
            <InputNome
              v-model="localData.Nome"
              label="Nome"
              :required="true"
              :erro="camposObrigatorios.erroNome"
            />
          </div>
        </div>

        <div class="form-grid-2">
          <div class="form-field">
            <label
              class="form-field-label form-field-label-required"
              for="tipoDocumento"
              >Tipo de Documento</label
            >
            <select
              class="field"
              id="tipoDocumento"
              v-model="localData.TipoDocumento"
              @change="aoMudarTipoDocumento"
            >
              <option value="">Selecione</option>
              <option value="CPF">CPF</option>
              <option value="CNPJ">CNPJ</option>
              <option value="CNH">CNH</option>
              <option value="RNE">RNE</option>
            </select>
            <p
              v-if="camposObrigatorios.erroTipoDocumento"
              class="form-field-error"
            >
              Campo obrigatório
            </p>
          </div>
          <div class="form-field">
            <label
              class="form-field-label form-field-label-required"
              for="documento"
              >{{ obterLabelDocumento }}</label
            >
            <input
              class="field"
              type="text"
              id="documento"
              v-model="localData.CpfCnpj"
              v-mask="obterMascaraDocumento"
              :placeholder="obterPlaceholderDocumento"
              @change="validarDocumento"
            />
            <p v-if="camposObrigatorios.erroCpfCnpj" class="form-field-error">
              Campo obrigatório
            </p>
            <p v-if="erroDocumento" class="form-field-error">
              {{ mensagemErro }}
            </p>
          </div>
        </div>

        <div class="form-grid-2">
          <div class="form-field">
            <label
              class="form-field-label form-field-label-required"
              for="email"
              >E-mail</label
            >
            <input
              class="field"
              type="text"
              id="email"
              v-model="localData.Email"
              @blur="validarEmail"
            />
            <p v-if="emailErros.email" class="form-field-error">
              {{ emailErros.email }}
            </p>
          </div>
          <div class="form-field">
            <telefone-internacional
              v-model="localData.Telefone"
              label="Telefone"
              name="Telefone"
              @validate="onValidateTelefone"
            />
            <p v-if="camposObrigatorios.erroTelefone" class="form-field-error">
              Campo obrigatório
            </p>
          </div>
        </div>

        <div class="form-grid-1">
          <div class="form-field">
            <label class="form-field-label" for="cargo">Cargo</label>
            <input
              class="field"
              type="text"
              id="cargo"
              v-model="localData.Cargo"
            />
          </div>
        </div>
      </div>

      <div class="form-subsection-divider"></div>

      <!-- SUBSECTION: Endereço -->
      <div class="form-subsection">
        <h4 class="form-subsection-title">
          <i class="fa fa-map-marker"></i>
          Endereço
        </h4>

        <div class="form-grid-2">
          <div class="form-field">
            <div class="flex items-center justify-between">
              <label
                class="form-field-label form-field-label-required"
                for="cep"
                >CEP</label
              >
              <a
                target="_blank"
                class="form-link text-xs"
                href="https://buscacepinter.correios.com.br/app/endereco/index.php"
                >Não sei o CEP</a
              >
            </div>
            <input
              class="field"
              type="text"
              id="cep"
              v-model="localData.Cep"
              @change="validarCep(localData)"
            />
            <p v-if="camposObrigatorios.erroCep" class="form-field-error">
              Campo obrigatório
            </p>
          </div>
          <div class="form-field">
            <label
              class="form-field-label form-field-label-required"
              for="numero"
              >Número</label
            >
            <input
              class="field"
              type="text"
              id="numero"
              v-model="localData.Numero"
            />
            <p v-if="camposObrigatorios.erroNumero" class="form-field-error">
              Campo obrigatório
            </p>
          </div>
        </div>

        <div class="form-grid-1">
          <div class="form-field">
            <label
              class="form-field-label form-field-label-required"
              for="logradouro"
              >Endereço/Logradouro</label
            >
            <input
              class="field"
              type="text"
              :disabled="isDisabledEnderecoLogradouro"
              id="logradouro"
              v-model="localData.Logradouro"
            />
            <p
              v-if="camposObrigatorios.erroLogradouro"
              class="form-field-error"
            >
              Campo obrigatório
            </p>
          </div>
        </div>

        <div class="form-grid-2">
          <div class="form-field">
            <label class="form-field-label" for="complemento"
              >Complemento</label
            >
            <input
              class="field"
              type="text"
              id="complemento"
              v-model="localData.Complemento"
            />
          </div>
          <div class="form-field">
            <label
              class="form-field-label form-field-label-required"
              for="bairro"
              >Bairro</label
            >
            <input
              class="field"
              type="text"
              :disabled="isDisabledEnderecoLogradouro"
              id="bairro"
              v-model="localData.Bairro"
            />
            <p v-if="camposObrigatorios.erroBairro" class="form-field-error">
              Campo obrigatório
            </p>
          </div>
        </div>

        <div class="form-grid-2">
          <div class="form-field">
            <label
              class="form-field-label form-field-label-required"
              for="cidade"
              >Cidade</label
            >
            <input
              class="field"
              type="text"
              :disabled="isDisabledEndereco"
              id="cidade"
              v-model="localData.Cidade"
            />
            <p v-if="camposObrigatorios.erroCidade" class="form-field-error">
              Campo obrigatório
            </p>
          </div>
          <div class="form-field">
            <label class="form-field-label form-field-label-required" for="uf"
              >UF</label
            >
            <select
              class="field"
              :disabled="isDisabledEndereco"
              id="uf"
              v-model="localData.Uf"
            >
              <option value="null">Selecione</option>
              <option v-for="uf in ufs" :key="uf" :value="uf">{{ uf }}</option>
            </select>
            <p v-if="camposObrigatorios.erroUF" class="form-field-error">
              Campo obrigatório
            </p>
          </div>
        </div>

        <div class="form-grid-1">
          <div class="form-field">
            <label class="form-field-label form-field-label-required" for="pais"
              >País</label
            >
            <select
              id="pais"
              class="field"
              v-model="localData.Pais"
              :disabled="isDisabledEndereco"
            >
              <option value="">Selecione</option>
              <option
                v-for="item in listaPaises"
                :key="item.Codigo"
                :value="item.Codigo"
              >
                {{ item.Descricao }}
              </option>
            </select>
            <p v-if="camposObrigatorios.erroPais" class="form-field-error">
              Campo obrigatório
            </p>
          </div>
        </div>
      </div>

      <div class="form-subsection-divider"></div>

      <!-- SUBSECTION: Informações Regulatórias -->
      <div class="form-subsection">
        <h4 class="form-subsection-title">
          <i class="fa fa-shield"></i>
          Informações Regulatórias
        </h4>

        <div class="form-grid-2">
          <div class="form-field">
            <label class="form-field-label form-field-label-required"
              >É considerado US Person?</label
            >
            <div class="form-radio-group">
              <label class="form-radio-item">
                <input
                  type="radio"
                  value="false"
                  name="usPerson"
                  class="form-radio-input"
                  v-model="localData.UsPerson"
                />
                <span class="form-radio-label">Não</span>
              </label>
              <label class="form-radio-item">
                <input
                  type="radio"
                  value="true"
                  name="usPerson"
                  class="form-radio-input"
                  v-model="localData.UsPerson"
                />
                <span class="form-radio-label">Sim</span>
              </label>
            </div>
          </div>
          <div class="form-field">
            <label class="form-field-label form-field-label-required"
              >É uma Pessoa Exposta Politicamente?</label
            >
            <div class="form-radio-group">
              <label class="form-radio-item">
                <input
                  type="radio"
                  value="false"
                  name="politicamenteExposta"
                  class="form-radio-input"
                  v-model="localData.PoliticamenteExposta"
                />
                <span class="form-radio-label">Não</span>
              </label>
              <label class="form-radio-item">
                <input
                  type="radio"
                  value="true"
                  name="politicamenteExposta"
                  class="form-radio-input"
                  v-model="localData.PoliticamenteExposta"
                />
                <span class="form-radio-label">Sim</span>
              </label>
            </div>
          </div>
        </div>

        <div class="form-grid-1">
          <div class="form-field">
            <label
              class="form-field-label form-field-label-required"
              for="assinatura"
              >Assinatura</label
            >
            <select
              id="assinatura"
              class="field"
              v-model="localData.Assinatura"
            >
              <option value="">Selecione</option>
              <option value="1">Não assina pela empresa</option>
              <option value="2">Assina isoladamente</option>
              <option value="0">Assina em conjunto</option>
            </select>
            <p
              v-if="camposObrigatorios.erroAssinatura"
              class="form-field-error"
            >
              Campo obrigatório
            </p>
          </div>
        </div>
      </div>
    </form>
  </FormModal>
</template>

<script>
import { mapGetters } from "vuex";
import FormModal from "./FormModal.vue";
import emailValidatorMixin from "@/utils/emailValidatorMixin.js";
import { validarCampoCep, ufs } from "@/utils/formUtils";
import { mask } from "vue-the-mask";
import documentoUtils from "@/utils/documentoUtils";

export default {
  name: "ModalProcurador",
  components: {
    FormModal,
  },
  directives: {
    mask,
  },
  mixins: [emailValidatorMixin],
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    procurador: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      listaPaises: "GET_LISTA_PAISES",
    }),
    isEditing() {
      return this.localData.Guid && this.localData.Guid !== "";
    },
    obterLabelDocumento() {
      if (!this.localData || !this.localData.TipoDocumento) return "CPF";
      return documentoUtils.obterLabelDocumento(this.localData.TipoDocumento);
    },
    obterMascaraDocumento() {
      if (!this.localData || !this.localData.TipoDocumento)
        return "###.###.###-##";
      return documentoUtils.obterMascaraDocumento(this.localData.TipoDocumento);
    },
    obterPlaceholderDocumento() {
      if (!this.localData || !this.localData.TipoDocumento)
        return "000.000.000-00";
      return documentoUtils.obterPlaceholderDocumento(
        this.localData.TipoDocumento,
      );
    },
    ufs() {
      return ufs;
    },
  },
  data() {
    return {
      telefoneValido: {},
      emailErros: {},
      erroDocumento: false,
      mensagemErro: "",
      isDisabledEndereco: false,
      isDisabledEnderecoLogradouro: false,
      camposObrigatorios: {
        erroNome: false,
        erroTipoDocumento: false,
        erroCpfCnpj: false,
        erroEmail: false,
        erroTelefone: false,
        erroCep: false,
        erroLogradouro: false,
        erroNumero: false,
        erroBairro: false,
        erroCidade: false,
        erroUF: false,
        erroPais: false,
        erroAssinatura: false,
      },
      localData: {
        Guid: "",
        Nome: "",
        CpfCnpj: "",
        TipoDocumento: "",
        Email: "",
        Cargo: "",
        Telefone: "",
        Cep: "",
        Logradouro: "",
        Numero: "",
        Complemento: "",
        Bairro: "",
        Cidade: "",
        Uf: "",
        Pais: "",
        Tipo: "Procurador",
        EmissorOrdem: false,
        UsPerson: false,
        PoliticamenteExposta: false,
        Assinatura: "",
      },
    };
  },
  watch: {
    procurador: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.localData = JSON.parse(JSON.stringify(newVal));
          if (this.localData.Cep) {
            this.validarCep(this.localData, true);
          }
        } else {
          this.resetForm();
        }
      },
    },
    visible(newVal) {
      if (!newVal) {
        this.resetValidation();
        if (!this.procurador) {
          this.resetForm();
        }
      }
    },
  },
  methods: {
    onValidateTelefone({ name, valid }) {
      if (!this.telefoneValido) {
        this.telefoneValido = {};
      }
      if (valid !== "undefined") {
        this.telefoneValido[name] = valid;
      }
    },
    validarEmail() {
      this.validarEmailGeneric(this.localData.Email, "email", {
        obrigatorio: true,
      });
    },
    aoMudarTipoDocumento() {
      if (!this.localData) return;
      this.localData.CpfCnpj = "";
      this.erroDocumento = false;
      this.mensagemErro = "";
    },
    validarDocumento() {
      if (!this.localData) return;

      if (this.localData.CpfCnpj) {
        const resultado = documentoUtils.validarDocumentoCompleto(
          this.localData.CpfCnpj,
          this.localData.TipoDocumento || "CPF",
        );
        this.erroDocumento = !resultado.valido;
        this.mensagemErro = resultado.mensagem;
      } else {
        this.erroDocumento = false;
        this.mensagemErro = "";
      }
    },
    async validarCep(objDados, somenteValidar = false) {
      const result = await validarCampoCep(objDados, {
        somenteValidar: somenteValidar,
      });
      if (result) {
        this.isDisabledEndereco = true;
        this.isDisabledEnderecoLogradouro = true;
        if (objDados.Logradouro === "") {
          this.isDisabledEnderecoLogradouro = false;
        }
      } else {
        this.isDisabledEndereco = false;
        this.isDisabledEnderecoLogradouro = false;
      }
    },
    validarFormulario() {
      let permitirAvancar = true;
      let nomeDoCampo = "";

      this.camposObrigatorios = {
        erroNome: false,
        erroTipoDocumento: false,
        erroCpfCnpj: false,
        erroEmail: false,
        erroTelefone: false,
        erroCep: false,
        erroLogradouro: false,
        erroNumero: false,
        erroBairro: false,
        erroCidade: false,
        erroUF: false,
        erroPais: false,
        erroAssinatura: false,
      };

      if (!this.localData.Nome) {
        this.camposObrigatorios.erroNome = true;
        nomeDoCampo = "Nome";
        permitirAvancar = false;
      }

      if (!this.localData.TipoDocumento) {
        this.camposObrigatorios.erroTipoDocumento = true;
        nomeDoCampo = nomeDoCampo || "Tipo de Documento";
        permitirAvancar = false;
      }

      if (!this.localData.CpfCnpj) {
        this.camposObrigatorios.erroCpfCnpj = true;
        nomeDoCampo = nomeDoCampo || this.obterLabelDocumento;
        permitirAvancar = false;
      } else if (this.erroDocumento) {
        this.camposObrigatorios.erroCpfCnpj = true;
        nomeDoCampo = nomeDoCampo || this.obterLabelDocumento;
        permitirAvancar = false;
      }

      if (Object.values(this.emailErros).some((erro) => erro !== "")) {
        this.camposObrigatorios.erroEmail = true;
        nomeDoCampo = nomeDoCampo || "E-mail";
        permitirAvancar = false;
      }

      if (!this.localData.Telefone || !this.telefoneValido?.Telefone) {
        this.camposObrigatorios.erroTelefone = true;
        nomeDoCampo = nomeDoCampo || "Telefone";
        permitirAvancar = false;
      }

      if (!this.localData.Cep) {
        this.camposObrigatorios.erroCep = true;
        nomeDoCampo = nomeDoCampo || "CEP";
        permitirAvancar = false;
      }

      if (!this.localData.Logradouro) {
        this.camposObrigatorios.erroLogradouro = true;
        nomeDoCampo = nomeDoCampo || "Logradouro";
        permitirAvancar = false;
      }

      if (!this.localData.Numero) {
        this.camposObrigatorios.erroNumero = true;
        nomeDoCampo = nomeDoCampo || "Número do endereço";
        permitirAvancar = false;
      }

      if (!this.localData.Bairro) {
        this.camposObrigatorios.erroBairro = true;
        nomeDoCampo = nomeDoCampo || "Bairro";
        permitirAvancar = false;
      }

      if (!this.localData.Cidade) {
        this.camposObrigatorios.erroCidade = true;
        nomeDoCampo = nomeDoCampo || "Cidade";
        permitirAvancar = false;
      }

      if (!this.localData.Uf) {
        this.camposObrigatorios.erroUF = true;
        nomeDoCampo = nomeDoCampo || "UF";
        permitirAvancar = false;
      }

      if (!this.localData.Pais) {
        this.camposObrigatorios.erroPais = true;
        nomeDoCampo = nomeDoCampo || "País";
        permitirAvancar = false;
      }

      if (
        this.localData.Assinatura === null ||
        this.localData.Assinatura === undefined ||
        this.localData.Assinatura === ""
      ) {
        this.camposObrigatorios.erroAssinatura = true;
        nomeDoCampo = nomeDoCampo || "Assinatura";
        permitirAvancar = false;
      }

      if (!permitirAvancar && nomeDoCampo) {
        this.$swal({
          icon: "warning",
          title: "Oops...",
          text: `O campo ${nomeDoCampo} não foi informado, preencha a informação para avançar.`,
        });
      }

      return permitirAvancar;
    },
    handleSubmit() {
      if (this.validarFormulario()) {
        this.$emit("submit", this.localData);
      }
    },
    handleClose() {
      this.$emit("update:visible", false);
      this.$emit("close");
    },
    resetForm() {
      this.localData = {
        Guid: "",
        Nome: "",
        CpfCnpj: "",
        TipoDocumento: "",
        Email: "",
        Cargo: "",
        Telefone: "",
        Cep: "",
        Logradouro: "",
        Numero: "",
        Complemento: "",
        Bairro: "",
        Cidade: "",
        Uf: "",
        Pais: "",
        Tipo: "Procurador",
        EmissorOrdem: false,
        UsPerson: false,
        PoliticamenteExposta: false,
        Assinatura: "",
      };
      this.resetValidation();
    },
    resetValidation() {
      this.camposObrigatorios = {
        erroNome: false,
        erroTipoDocumento: false,
        erroCpfCnpj: false,
        erroEmail: false,
        erroTelefone: false,
        erroCep: false,
        erroLogradouro: false,
        erroNumero: false,
        erroBairro: false,
        erroCidade: false,
        erroUF: false,
        erroPais: false,
        erroAssinatura: false,
      };
      this.emailErros = {};
      this.erroDocumento = false;
      this.mensagemErro = "";
      this.isDisabledEndereco = false;
      this.isDisabledEnderecoLogradouro = false;
    },
  },
};
</script>
